import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import {
  ChartLine,
  TrendUp,
  TrendDown,
  Clock,
  CurrencyCircleDollar,
  CheckCircle,
  Target,
  CalendarBlank,
  Medal,
  Trophy,
  Star,
  Package,
  Warning,
} from '@phosphor-icons/react'

export default function PerformancePage() {
  const { user } = useAuth()
  const { t } = useLanguage()

  // TODO: Implement period filter state
  const [selectedPeriod, setSelectedPeriod] = useState<'7days' | '30days' | '90days' | '1year'>('30days')

  // TODO: Replace with real API data
  // Mock metrics for the selected period
  const metrics = {
    winRate: 62.5,
    winRateTrend: 8.3,
    avgResponseTime: 2.4,
    avgResponseTimeTrend: -15.2,
    conversionRate: 58,
    conversionRateTrend: 12,
    totalRevenue: 125000,
    totalRevenueTrend: 28,
  }

  // TODO: Fetch from API - Performance breakdown
  const performanceData = {
    proposals: {
      total: 48,
      accepted: 30,
      rejected: 12,
      pending: 6,
    },
    responseTimeBreakdown: {
      under1h: 15,
      between1and4h: 20,
      between4and24h: 10,
      over24h: 3,
    },
    modalityPerformance: [
      { name: 'Marítimo', proposals: 28, won: 18, winRate: 64.3, revenue: 85000 },
      { name: 'Aéreo', proposals: 12, won: 8, winRate: 66.7, revenue: 28000 },
      { name: 'Terrestre', proposals: 8, won: 4, winRate: 50, revenue: 12000 },
    ],
    topClients: [
      { name: 'Tech Import Ltda', shipments: 12, revenue: 45000, satisfaction: 4.8 },
      { name: 'Global Exports SA', shipments: 8, revenue: 32000, satisfaction: 4.9 },
      { name: 'Import Solutions', shipments: 6, revenue: 28000, satisfaction: 4.6 },
      { name: 'Mercosur Logistics', shipments: 4, revenue: 20000, satisfaction: 4.5 },
    ],
    monthlyTrend: [
      { month: 'Jul', proposals: 12, won: 7, revenue: 35000 },
      { month: 'Ago', proposals: 15, won: 9, revenue: 42000 },
      { month: 'Set', proposals: 18, won: 11, revenue: 48000 },
      { month: 'Out', proposals: 16, won: 10, revenue: 45000 },
      { month: 'Nov', proposals: 20, won: 13, revenue: 58000 },
    ],
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Period labels
  const periodLabels = {
    '7days': 'Últimos 7 dias',
    '30days': 'Últimos 30 dias',
    '90days': 'Últimos 90 dias',
    '1year': 'Último ano',
  }

  // Calculate win rate color
  const getWinRateColor = (rate: number) => {
    if (rate >= 60) return 'text-emerald-600'
    if (rate >= 40) return 'text-amber-600'
    return 'text-red-600'
  }

  const getWinRateBg = (rate: number) => {
    if (rate >= 60) return 'bg-emerald-600'
    if (rate >= 40) return 'bg-amber-600'
    return 'bg-red-600'
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Performance
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="gap-2 h-9">
                <CalendarBlank size={16} />
                Personalizar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 font-medium">Período:</span>
            {Object.entries(periodLabels).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setSelectedPeriod(value as any)}
                className={`px-3 h-9 text-sm font-medium rounded-md transition-colors ${
                  selectedPeriod === value
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Top KPIs - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Win Rate */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Taxa de Vitória
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className={`text-3xl font-bold ${getWinRateColor(metrics.winRate)}`}>
                  {metrics.winRate}%
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-emerald-600">+{metrics.winRateTrend}%</span> vs período anterior
                </p>
              </div>
              <Trophy size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Response Time */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Tempo de Resposta
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{metrics.avgResponseTime}h</p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-emerald-600">{metrics.avgResponseTimeTrend}%</span> vs período anterior
                </p>
              </div>
              <Clock size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Taxa de Conversão
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{metrics.conversionRate}%</p>
                <p className="text-sm text-gray-600 mt-1">
                  Oportunidades → Propostas
                </p>
              </div>
              <Target size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>
        </div>

        {/* Revenue & Proposals - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Total Revenue */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Receita Total
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.totalRevenue)}</p>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="font-medium text-emerald-600">+{metrics.totalRevenueTrend}%</span> no período
                </p>
              </div>
              <CurrencyCircleDollar size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Proposals Breakdown */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 col-span-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Análise de Propostas
            </p>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{performanceData.proposals.total}</p>
                <p className="text-xs text-gray-600 mt-1">Total</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{performanceData.proposals.accepted}</p>
                <p className="text-xs text-gray-600 mt-1">Aceitas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{performanceData.proposals.rejected}</p>
                <p className="text-xs text-gray-600 mt-1">Rejeitadas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{performanceData.proposals.pending}</p>
                <p className="text-xs text-gray-600 mt-1">Pendentes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Response Time Distribution */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
              Distribuição de Tempo de Resposta
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-emerald-500" weight="fill" />
                  <span className="text-sm text-gray-700">Menos de 1h</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full"
                      style={{ width: `${(performanceData.responseTimeBreakdown.under1h / 48) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">
                    {performanceData.responseTimeBreakdown.under1h}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-blue-500" weight="fill" />
                  <span className="text-sm text-gray-700">1h - 4h</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(performanceData.responseTimeBreakdown.between1and4h / 48) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">
                    {performanceData.responseTimeBreakdown.between1and4h}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock size={16} className="text-amber-500" weight="fill" />
                  <span className="text-sm text-gray-700">4h - 24h</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-amber-500 h-2 rounded-full"
                      style={{ width: `${(performanceData.responseTimeBreakdown.between4and24h / 48) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">
                    {performanceData.responseTimeBreakdown.between4and24h}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Warning size={16} className="text-red-500" weight="fill" />
                  <span className="text-sm text-gray-700">Mais de 24h</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-red-500 h-2 rounded-full"
                      style={{ width: `${(performanceData.responseTimeBreakdown.over24h / 48) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">
                    {performanceData.responseTimeBreakdown.over24h}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Clients */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
              Top Clientes
            </h3>
            <div className="space-y-4">
              {performanceData.topClients.map((client, index) => (
                <div
                  key={index}
                  className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-emerald-700">
                        {index + 1}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {client.name}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>{client.shipments} embarques</span>
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-amber-500 fill-current" />
                      <span className="text-xs text-gray-700">{client.satisfaction}</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-emerald-600">
                    {formatCurrency(client.revenue)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance by Modality */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Performance por Modalidade
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Modalidade
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propostas
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ganhas
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taxa de Vitória
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receita
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {performanceData.modalityPerformance.map((modality, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <span className="font-medium text-sm text-gray-900">{modality.name}</span>
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-700">
                      {modality.proposals}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                        <CheckCircle size={12} weight="fill" />
                        {modality.won}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getWinRateBg(modality.winRate)}`}
                            style={{ width: `${modality.winRate}%` }}
                          />
                        </div>
                        <span className={`text-sm font-semibold ${getWinRateColor(modality.winRate)}`}>
                          {modality.winRate.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-emerald-600">
                      {formatCurrency(modality.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
            Tendência Mensal
          </h3>
          <div className="space-y-3">
            {performanceData.monthlyTrend.map((month, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-900 w-12">
                  {month.month}
                </span>
                <div className="flex-1 mx-4">
                  <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                    <span>{month.proposals} propostas</span>
                    <span>•</span>
                    <span className="text-emerald-600">{month.won} ganhas</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full"
                      style={{ width: `${(month.won / month.proposals) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900 w-24 text-right">
                  {formatCurrency(month.revenue)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
